/* eslint-disable camelcase */
import React, { useState } from "react"
import { Box, Grid, TextField, InputAdornment, Typography, CircularProgress, Paper, Pagination } from "@mui/material"
import SearchIcon from "@mui/icons-material/Search"
import StorefrontIcon from "@mui/icons-material/Storefront"
import ProductoCard from "./ProductoCard"

const CatalogoProductos = ({ catalogo, carrito, setFormData, loading }) => {
  const [searchTerm, setSearchTerm] = useState("")
  const [page, setPage] = useState(1)

  // Constante para definir el número de ítems por página
  const ITEMS_PER_PAGE = 20

  // Buscador local: Filtra instantáneamente por código o descripción
  const catalogoFiltrado = catalogo.filter((prod) => {
    const termino = searchTerm.toLowerCase()
    return prod.artdescri.toLowerCase().includes(termino) || prod.artcodigo.toLowerCase().includes(termino)
  })

  // Lógica de Paginación en memoria
  const totalPages = Math.ceil(catalogoFiltrado.length / ITEMS_PER_PAGE)
  const startIndex = (page - 1) * ITEMS_PER_PAGE
  const paginatedCatalogo = catalogoFiltrado.slice(startIndex, startIndex + ITEMS_PER_PAGE)

  // Función unificada para manejar la inserción/actualización en el carrito
  const handleUpdateCantidad = (producto, nuevaCantidad) => {
    setFormData((prev) => {
      const nuevoDetalle = [...prev.detalle]

      // Buscamos si el artículo ya está en el carrito
      const index = nuevoDetalle.findIndex(
        (item) => item.artcodigo === producto.artcodigo && item.invcodigo === producto.invcodigo,
      )

      if (nuevaCantidad > 0) {
        if (index >= 0) {
          // Si ya existe, actualizamos la cantidad
          nuevoDetalle[index].cantidad_solicitada = nuevaCantidad
        } else {
          // Si no existe, lo agregamos como un nuevo objeto
          nuevoDetalle.push({
            ...producto,
            cantidad_solicitada: nuevaCantidad,
          })
        }
      } else {
        // Si la cantidad es 0 o menor, lo sacamos del carrito
        if (index >= 0) {
          nuevoDetalle.splice(index, 1)
        }
      }

      return { ...prev, detalle: nuevoDetalle }
    })
  }

  // Helper para saber qué cantidad tiene un producto específico en el carrito actual
  const getCantidadEnCarrito = (producto) => {
    const item = carrito.find((c) => c.artcodigo === producto.artcodigo && c.invcodigo === producto.invcodigo)
    return item ? parseInt(item.cantidad_solicitada, 10) : 0
  }

  // Manejador del buscador que también reinicia la paginación a la página 1
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value)
    setPage(1)
  }

  // Manejador para el cambio de página
  const handlePageChange = (event, value) => {
    setPage(value)
  }

  return (
    <Paper elevation={2} sx={{ p: 4, borderRadius: 3, background: "white", minHeight: "500px" }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        borderBottom="2px solid #196C87"
        pb={1}
      >
        <Typography variant="h6" color="primary">
          Catálogo de Productos Disponibles
        </Typography>

        <TextField
          variant="outlined"
          size="small"
          placeholder="Buscar por código o nombre..."
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{ width: "350px" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {loading ? (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="300px">
          <CircularProgress size={50} thickness={4} />
          <Typography variant="body1" color="textSecondary" mt={2}>
            Cargando catálogo de la bodega...
          </Typography>
        </Box>
      ) : catalogo.length === 0 ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="300px"
          color="text.secondary"
        >
          <StorefrontIcon sx={{ fontSize: 60, mb: 1, opacity: 0.5 }} />
          <Typography variant="h6">No hay productos disponibles</Typography>
          <Typography variant="body2">No se encontraron productos asignados a su bodega en el sistema.</Typography>
        </Box>
      ) : catalogoFiltrado.length === 0 ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="300px"
          color="text.secondary"
        >
          <SearchIcon sx={{ fontSize: 60, mb: 1, opacity: 0.5 }} />
          <Typography variant="h6">Búsqueda sin resultados</Typography>
          <Typography variant="body2">No encontramos ningún producto que coincida con "{searchTerm}".</Typography>
        </Box>
      ) : (
        <>
          <Grid container spacing={3}>
            {paginatedCatalogo.map((producto) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={`${producto.invcodigo}-${producto.artcodigo}`}>
                <ProductoCard
                  producto={producto}
                  cantidadEnCarrito={getCantidadEnCarrito(producto)}
                  onUpdateCantidad={handleUpdateCantidad}
                />
              </Grid>
            ))}
          </Grid>

          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={4} pt={2}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}
    </Paper>
  )
}

export default CatalogoProductos
